import type { DeviceResponseV5 } from '@emma/api_client/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDevices } from '@/hooks/device';
import { useApiClient } from '../api';
import type { RolloutWindowResponse } from './rollout_windows';

export type DeviceRolloutSummary = {
  device: DeviceResponseV5;
  rolloutWindows: RolloutWindowResponse[];
};

export type FleetRolloutWindowStats = {
  configured: number;
  active: number;
  unconfigured: number;
  total: number;
  isLoading: boolean;
  failedCount: number;
  deviceSummaries: DeviceRolloutSummary[];
  loadStats: () => void;
};

export function useFleetRolloutWindowStats(
  workspaceId: number,
  fleetId: number
): FleetRolloutWindowStats {
  const client = useApiClient();

  const { data: devicesData, isLoading: devicesLoading } = useDevices({
    fleetIdIn: [fleetId],
    workspaceIdIn: [workspaceId],
    limit: 1000,
  });

  const [deviceSummaries, setDeviceSummaries] = useState<DeviceRolloutSummary[]>([]);
  const [windowsLoading, setWindowsLoading] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [fetchVersion, setFetchVersion] = useState(0);

  const devices = useMemo(() => devicesData?.devices ?? [], [devicesData]);

  const loadStats = useCallback(() => {
    setFetchVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (fetchVersion === 0 || devicesLoading || devices.length === 0) {
      if (fetchVersion > 0 && !devicesLoading && devices.length === 0) {
        setDeviceSummaries([]);
        setWindowsLoading(false);
        setFailedCount(0);
      }
      return;
    }

    let cancelled = false;
    setWindowsLoading(true);
    setFailedCount(0);

    const fetchAll = async () => {
      const results: DeviceRolloutSummary[] = [];
      const concurrency = 10;
      let failures = 0;

      for (let i = 0; i < devices.length; i += concurrency) {
        const batch = devices.slice(i, i + concurrency);
        const batchResults = await Promise.all(
          batch.map(async (device) => {
            try {
              const response = await client.v1.workspaces[':workspaceId'].devices[':deviceId'][
                'rollout-windows'
              ].$get({
                param: { workspaceId: workspaceId.toString(), deviceId: device.id.toString() },
                query: {},
              });

              if (response.status !== 200) {
                return { device, rolloutWindows: [] as RolloutWindowResponse[], failed: true };
              }

              const body = await response.json();
              return {
                device,
                rolloutWindows: body.rolloutWindows,
                failed: false,
              };
            } catch {
              return { device, rolloutWindows: [] as RolloutWindowResponse[], failed: true };
            }
          })
        );
        for (const r of batchResults) {
          results.push({ device: r.device, rolloutWindows: r.rolloutWindows });
          if (r.failed) failures++;
        }
      }

      if (!cancelled) {
        setDeviceSummaries(results);
        setWindowsLoading(false);
        setFailedCount(failures);
      }
    };

    void fetchAll();

    return () => {
      cancelled = true;
    };
  }, [devices, devicesLoading, workspaceId, client, fetchVersion]);

  const total = devices.length;
  const configured = deviceSummaries.filter((s) => s.rolloutWindows.length > 0).length;
  const active = deviceSummaries.filter((s) =>
    s.rolloutWindows.some((w) => w.status === 'ACTIVE')
  ).length;
  const unconfigured = total - configured;

  return {
    configured,
    active,
    unconfigured,
    total,
    isLoading: devicesLoading || windowsLoading,
    failedCount,
    deviceSummaries,
    loadStats,
  };
}
